// Financing inputs + math for one product. The rules themselves live in `shared/financing`,
// so the boundary the customer sees is the code `validate-checkout` enforces.

import { formatUsd, minDownPayment, minDownPaymentLabel, type FinanceLimits } from 'shared';

/** Null only for unparseable or negative input. Blank is 0, which the floor then rejects. */
export function parseDownPayment(raw: string): number | null {
  const cleaned = raw.replace(/[$,\s]/g, '');
  if (cleaned === '') return 0;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

/** Balance after the down payment, spread evenly across the term. */
export function monthlyEstimate(
  total: number,
  downPayment: number,
  durationMonths: number,
): number {
  return Math.max(total - downPayment, 0) / durationMonths;
}

/** `wholesale` excludes tax — the same pre-tax basis the server checks against. */
export function validateDownPayment(
  amount: number | null,
  wholesale: number,
  limits: FinanceLimits,
): string | null {
  if (amount === null) return 'Enter a dollar amount.';
  const floor = minDownPayment(wholesale, limits);
  if (amount < floor)
    return `Financing needs at least ${formatUsd(floor)} down (${minDownPaymentLabel(limits)}).`;
  if (amount >= wholesale) return 'Down payment must be less than your plan price before tax.';
  return null;
}

/** What the field opens at, so the customer starts on a valid amount. */
export function defaultDownPaymentInput(wholesale: number, limits: FinanceLimits): string {
  return minDownPayment(wholesale, limits).toFixed(2);
}
